import {
    Box,
    Grid,
    Typography,
    SxProps,
    Theme,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    IconButton,
    Paper,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTestingStandardSearch } from "../hooks";
import ReinovaButton from "../components/ReinovaButton";
import InfoBox from "../components/InfoBox";
import PageContent from "../components/PageContent";
import { useIsMobile } from "../hooks";
import { useTranslation } from "react-i18next";
import backImg from "../assets/back.webp";
import backImgMobile from "../assets/back-mobile.webp";
import { CommonStyles } from "../styles/CommonStyles";

const TestingStandardPageStyle: Record<string, SxProps<Theme>> = {
    standardTitle: {
        textAlign: "left",
        fontSize: "1.5rem",
    },
    standardSubtitle: {
        textAlign: "left",
        fontSize: "1.3rem",
    },
    contactText: {
        fontSize: "1.3rem",
        textAlign: "center",
        marginBottom: "20px",
    },
    paperContainer: (theme: Theme) => ({
        background: "linear-gradient(to bottom, #383249, #2F2A3E)",
        marginBottom: "20px",
        padding: "25px 5px 5px 25px"
    }),
    standardContainer: (theme: Theme) => ({
        boxShadow: "0px 4px 4px 0px #00000040",
        borderRadius: "16px",
        background: theme.palette.primary.main,
        padding: "40px",
        marginTop: "30px",
        marginLeft: "30px",
    }),
    standardMobileContainer: (theme: Theme) => ({
        // boxShadow: "0px 4px 4px 0px #00000040",
        // borderRadius: "16px",
        marginTop: "30px",
        marginLeft: "30px",
        // border: "1px solid blue"
    }),
    nonscrollableMobileContainer: (theme: Theme) => ({
        // paddingRight: "20px",
    }),
    scrollableContainer: (theme: Theme) => ({
        height: "400px",
        paddingRight: "20px",
        // marginBottom: "30px",
        overflow: "scroll",
        "&::-webkit-scrollbar": {
            borderRadius: 2,
            width: "0.5em",
            border: "1px solid white",
        },
        "&::-webkit-scrollbar-track": {
            background: "#81BC0047",
            borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb": {
            background: "#81BC00",
            borderRadius: "5px",
            border: "1px solid white",
        },
        "&::-webkit-scrollbar-thumb:hover": {
            background: "#81BC00",
        },
    }),
    pointList: (theme: Theme) => ({
        background: theme.palette.secondary.main,
        width: "15px",
        height: "15px",
        borderRadius: "7.5px",
    }),
    cardButton: {
        color: "white",
        marginLeft: "0px !important",
    },
    contactContainer: (theme: Theme) => ({
        display: "flex",
        flex: 1,
        height: "100%",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        borderLeft: `1px solid ${theme.palette.secondary.main}`,
    }),
    contactContainerMobile: (theme: Theme) => ({
        display: "flex",
        flex: 1,
        height: "100%",
        marginTop: "20px",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        borderTop: `1px solid ${theme.palette.secondary.main}`,
    })
};

type TestingStandardPageParams = {
    standardQuery: string;
};

const TestingStandardPage = () => {
    const { standardQuery } = useParams<TestingStandardPageParams>();
    const { result } = useTestingStandardSearch(standardQuery ?? "");
    const { isMobile, windowWidth } = useIsMobile();
    const { t } = useTranslation();

    let navigate = useNavigate();

    return (
        <>
            <PageContent>
                <Grid container spacing={4} sx={{ alignItems: "center" }}>

                    {/* Pulsante back e "risultati ricerca per..." */}
                    <Grid container item md={12} alignItems={"center"}>
                        <IconButton
                            size="large"
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            sx={isMobile ? CommonStyles.backMobile : CommonStyles.back}
                            onClick={() => navigate("/testing")}
                        >
                            <img src={isMobile ? backImgMobile : backImg} />
                        </IconButton>
                        <Typography sx={TestingStandardPageStyle.standardTitle}>
                            {t("testingstandard.result")}{standardQuery}
                        </Typography>
                    </Grid>

                    <Grid md={12}>
                        <Box sx={isMobile ? TestingStandardPageStyle.standardMobileContainer : TestingStandardPageStyle.standardContainer}>
                            <Grid container md={12}>
                                <Grid md={8} sm={12}>
                                    {/* <Grid md={12}> */}
                                        {!result || (result.results.length === 0 && <>
                                            <Typography
                                                sx={[
                                                    TestingStandardPageStyle.standardSubtitle,
                                                    { marginBottom: "30px" },
                                                ].flat(2)}
                                                >
                                                {t("testingstandard.notfound")}
                                            </Typography>
                                            <ReinovaButton onClick={() => navigate("/contact")}>
                                            {t("testingstandard.contact")}
                                        </ReinovaButton>
                                        </>
                                        )}

                                        <Box sx={isMobile ? TestingStandardPageStyle.nonscrollableMobileContainer : TestingStandardPageStyle.scrollableContainer}>
                                            {result && result.results.length !== 0 && result.results.map(item =>
                                                <Paper elevation={5} sx={TestingStandardPageStyle.paperContainer}>
                                                    <Typography
                                                        sx={[
                                                            TestingStandardPageStyle.standardSubtitle,
                                                            isMobile ? { fontSize: "18px" } : {},
                                                        ].flat(2)}
                                                    >
                                                        {item.standard}
                                                    </Typography>
                                                    <Typography
                                                        sx={[
                                                            TestingStandardPageStyle.standardSubtitle,
                                                            isMobile ? { fontSize: "18px" } : {},
                                                        ].flat(2)}
                                                    >
                                                        {t("testingstandard.areascovered")}
                                                    </Typography>
                                                    <List sx={{ marginLeft: isMobile ? "0px" : "20px" }}>
                                                        {item.fields.map((res) => (
                                                            <ListItem>
                                                                <ListItemAvatar sx={{ minWidth: "30px" }}>
                                                                    <Box
                                                                        sx={TestingStandardPageStyle.pointList}
                                                                    ></Box>
                                                                </ListItemAvatar>
                                                                <ListItemText
                                                                    primary={res}
                                                                    primaryTypographyProps={{
                                                                        fontSize: isMobile ? "18px" : "1.3rem",
                                                                    }}
                                                                />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </Paper>
                                            )}
                                        </Box>
                                    {/* </Grid> */}
                                </Grid>

                                { !isMobile && result && result.results.length > 0 && <Grid md={4} sm={12}>
                                    <Box sx={TestingStandardPageStyle.contactContainer}>
                                        <Typography
                                            sx={[
                                                TestingStandardPageStyle.contactText,
                                                isMobile ? { fontSize: "18px" } : {},
                                            ].flat(2)}
                                        >
                                            {t("testingstandard.salesteam")}
                                        </Typography>
                                        <ReinovaButton onClick={() => navigate("/contact")}>
                                            {t("testingstandard.contact")}
                                        </ReinovaButton>
                                    </Box>
                                </Grid>}
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
                
                {/* casella contatto */}
                { result && result.results.length > 0 && <Grid container sx={{ alignItems: "center"}}>
                        <Grid md={2} sm={0}></Grid>
                        <Grid md={8} sm={12} sx={{ marginTop: "30px" }}>
                            <InfoBox />
                        </Grid>
                        <Grid md={2} sm={0}></Grid>
                    </Grid>
                }
            </PageContent>
        </>
    );
};

export default TestingStandardPage;
